import { isSemanticSuggestionsOpenAtom } from '@components/listings-search-bar/atoms';
import { useAppSelector } from '@redux/hooks';
import { selectIsMobile } from '@redux/viewport/selectors';
import { IconAction } from '@tkww/orion-web-ds-action';
import { Tooltip } from '@tkww/orion-web-ds-feedback';
import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { searchBarTooltipV1CookieAtom } from '../atoms';
import CloseIcon from './CloseIcon';
import Styles from './SearchBarTooltip.styles.scss';
import { useTrackSearchBarTooltip } from './useTrackSearchBarTooltip';

export const SearchBarTooltip = () => {
	const isMobile = useAppSelector((state) => selectIsMobile(state));
	const [cookie, setCookie] = useAtom(searchBarTooltipV1CookieAtom);
	const typeaheadOpen = useAtomValue(isSemanticSuggestionsOpenAtom);
	const [isClosed, setIsClosed] = useState(false);
	const { trackShown, trackDismissed } = useTrackSearchBarTooltip();
	const hasTrackedShown = useRef(false);
	const hasTrackedDismissed = useRef(false);

	const dismiss = useCallback(() => {
		setIsClosed(true);
		setCookie({ dismissed: true });
	}, [setCookie]);

	// Only explicit user actions (X button, Escape) count as a tracked
	// dismissal — focusing the search bar dismisses the tooltip too, but
	// isn't a deliberate "close this" action, so it isn't tracked.
	const dismissAndTrack = useCallback(() => {
		dismiss();
		if (!hasTrackedDismissed.current) {
			hasTrackedDismissed.current = true;
			trackDismissed();
		}
	}, [dismiss, trackDismissed]);

	// Whether the tooltip would be showing if the search typeahead weren't
	// open. Used below to treat focusing the search bar as a dismissal too,
	// not just a temporary hide.
	const wouldBeVisible = Boolean(cookie && !cookie.dismissed && !isClosed);
	const isVisible = wouldBeVisible && !typeaheadOpen;

	useEffect(() => {
		if (typeaheadOpen && wouldBeVisible) {
			dismiss();
		}
	}, [typeaheadOpen, wouldBeVisible, dismiss]);

	useEffect(() => {
		if (isVisible && !hasTrackedShown.current) {
			hasTrackedShown.current = true;
			trackShown();
		}
	}, [isVisible, trackShown]);

	useEffect(() => {
		if (!isVisible) {
			return;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				dismissAndTrack();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isVisible, dismissAndTrack]);

	if (!isVisible) {
		return null;
	}

	return (
		<Tooltip
			header="Find your team faster"
			arrowPosition="top"
			// Figma spec uses a different arrow position per breakpoint: centered
			// on mobile, but offset left (matching the beak's 12px/32px position
			// within the 320px box → 28/320 = 8.75%) on desktop. Tooltip.d.ts
			// declares arrowOffset as a plain 0-100 number, not a percent string.
			arrowOffset={isMobile ? 50 : 8.75}
			className={Styles.tooltip}
			// This tooltip appears on its own (not from a user action) and
			// never moves focus, so role="dialog" would be misleading — it
			// implies focus/interaction semantics we don't provide. Use a
			// live region instead so screen readers announce it without
			// disrupting focus in the search input.
			aria-live="polite"
			aria-label="Find your team faster"
		>
			<IconAction
				className={Styles.closeButton}
				onClick={dismissAndTrack}
				aria-label="Dismiss tip"
			>
				<CloseIcon />
			</IconAction>
			Narrow it down by searching specific vendors or styles, like &quot;rustic
			barn.&quot;
		</Tooltip>
	);
};
